/**
 * Sortable row for a transaction custom field in settings.
 * Desktop: table row (like CurrencySettings). Mobile: card (Paper) like currency cards.
 */

import { ReactElement, useState, useCallback } from "react";
import {
  Box,
  IconButton,
  Stack,
  Typography,
  Chip,
  Tooltip,
  TableCell,
  TableRow,
  Paper,
} from "@mui/material";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import EditIcon from "@mui/icons-material/Edit";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import DeleteIcon from "@mui/icons-material/Delete";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import type { TransactionCustomField } from "../../../types";
import { TransactionFieldIcon } from "./TransactionFieldIcon";

/** Shared column widths: Type 25%, For 10%. */
export const TRANSACTION_FIELD_TABLE_CELL_SX = {
  order: { width: "8%", minWidth: 48, borderColor: "divider" as const },
  fieldName: { width: "27%", minWidth: 100, borderColor: "divider" as const },
  type: { width: "25%", minWidth: 80, borderColor: "divider" as const },
  for: { width: "10%", minWidth: 70, borderColor: "divider" as const },
  actions: { width: "30%", minWidth: 100, borderColor: "divider" as const },
};

export interface SortableTransactionFieldRowProps {
  field: TransactionCustomField;
  forList: string;
  inUse: boolean;
  /** When true, delete is disabled (e.g. still loading usage). */
  usageLoading?: boolean;
  saving: boolean;
  onEdit: (field: TransactionCustomField) => void;
  onHide: (field: TransactionCustomField) => void;
  onDeleteClick: (field: TransactionCustomField) => void;
  isMobile?: boolean;
  readOnly?: boolean;
}

function ActionsCell({
  field,
  saving,
  usageLoading = false,
  inUse,
  onEdit,
  onHide,
  onDeleteClick,
  isMobile,
  readOnly = false,
}: {
  field: TransactionCustomField;
  saving: boolean;
  usageLoading?: boolean;
  inUse: boolean;
  onEdit: (f: TransactionCustomField) => void;
  onHide: (f: TransactionCustomField) => void;
  onDeleteClick: (f: TransactionCustomField) => void;
  isMobile?: boolean;
  readOnly?: boolean;
}): ReactElement {
  const [deleteTooltipOpen, setDeleteTooltipOpen] = useState(false);
  const deleteDisabled = readOnly || saving || usageLoading || inUse;
  const deleteTooltip =
    usageLoading
      ? "Loading…"
      : inUse
        ? "Cannot delete: this field is used by transactions. Hide it instead, or remove the field from existing transactions first."
        : "Delete";

  const handleDeleteWrapClick = useCallback(() => {
    if (!isMobile || !deleteDisabled) return;
    setDeleteTooltipOpen(true);
    setTimeout(() => setDeleteTooltipOpen(false), 2500);
  }, [isMobile, deleteDisabled]);

  const actions = (
    <>
      <IconButton aria-label="Edit" onClick={() => onEdit(field)} disabled={readOnly || saving} size="small" sx={isMobile ? { p: 0.25 } : undefined}>
        <EditIcon fontSize="small" sx={isMobile ? { fontSize: 18 } : undefined} />
      </IconButton>
      <Tooltip title="Hide" arrow placement={isMobile ? "top" : "left"}>
        <span>
          <IconButton aria-label="Hide" onClick={() => onHide(field)} disabled={readOnly || saving} size="small" sx={isMobile ? { p: 0.25 } : undefined}>
            <VisibilityOffIcon fontSize="small" sx={isMobile ? { fontSize: 18 } : undefined} />
          </IconButton>
        </span>
      </Tooltip>
      <Tooltip
        title={deleteTooltip}
        arrow
        placement={isMobile ? "top" : "left"}
        open={isMobile && deleteDisabled ? deleteTooltipOpen : undefined}
        onClose={() => setDeleteTooltipOpen(false)}
        disableFocusListener={isMobile && deleteDisabled}
        disableHoverListener={isMobile && deleteDisabled}
        disableTouchListener={isMobile && deleteDisabled}
      >
        <span style={{ display: "inline-flex" }}>
          <IconButton
            aria-label={deleteDisabled ? deleteTooltip : "Delete"}
            onClick={() => {
              if (isMobile && deleteDisabled) {
                handleDeleteWrapClick();
              } else if (!deleteDisabled) {
                onDeleteClick(field);
              }
            }}
            disabled={!isMobile && deleteDisabled}
            size="small"
            color={inUse ? "default" : "error"}
            sx={{
              ...(isMobile ? { p: 0.25 } : {}),
              ...(deleteDisabled ? { color: "text.secondary", "&.Mui-disabled": { color: "text.secondary", opacity: 1 } } : {}),
            }}
          >
            <DeleteIcon fontSize="small" sx={isMobile ? { fontSize: 18 } : undefined} />
          </IconButton>
        </span>
      </Tooltip>
    </>
  );

  if (!isMobile) {
    return (
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 0 }}>
        {actions}
      </Box>
    );
  }
  return actions;
}

/** Actions for a hidden field row: Edit, Unhide, Delete (same layout as visible row actions). */
function ActionsCellForHidden({
  field,
  saving,
  usageLoading = false,
  inUse,
  onEdit,
  onUnhide,
  onDeleteClick,
  isMobile,
  readOnly = false,
}: {
  field: TransactionCustomField;
  saving: boolean;
  usageLoading?: boolean;
  inUse: boolean;
  onEdit: (f: TransactionCustomField) => void;
  onUnhide: (f: TransactionCustomField) => void;
  onDeleteClick: (f: TransactionCustomField) => void;
  isMobile?: boolean;
  readOnly?: boolean;
}): ReactElement {
  const [deleteTooltipOpen, setDeleteTooltipOpen] = useState(false);
  const deleteDisabled = readOnly || saving || usageLoading || inUse;
  const deleteTooltip =
    usageLoading
      ? "Loading…"
      : inUse
        ? "Cannot delete: this field is used by transactions. Unhide it first, or remove the field from existing transactions first."
        : "Delete";

  const handleDeleteWrapClick = useCallback(() => {
    if (!isMobile || !deleteDisabled) return;
    setDeleteTooltipOpen(true);
    setTimeout(() => setDeleteTooltipOpen(false), 2500);
  }, [isMobile, deleteDisabled]);

  const actions = (
    <>
      <IconButton aria-label="Edit" onClick={() => onEdit(field)} disabled={readOnly || saving} size="small" sx={isMobile ? { p: 0.25 } : undefined}>
        <EditIcon fontSize="small" sx={isMobile ? { fontSize: 18 } : undefined} />
      </IconButton>
      <Tooltip title="Unhide" arrow placement={isMobile ? "top" : "left"}>
        <span>
          <IconButton aria-label="Unhide" onClick={() => onUnhide(field)} disabled={readOnly || saving} size="small" color="primary" sx={isMobile ? { p: 0.25 } : undefined}>
            <VisibilityIcon fontSize="small" sx={isMobile ? { fontSize: 18 } : undefined} />
          </IconButton>
        </span>
      </Tooltip>
      <Tooltip
        title={deleteTooltip}
        arrow
        placement={isMobile ? "top" : "left"}
        open={isMobile && deleteDisabled ? deleteTooltipOpen : undefined}
        onClose={() => setDeleteTooltipOpen(false)}
        disableFocusListener={isMobile && deleteDisabled}
        disableHoverListener={isMobile && deleteDisabled}
        disableTouchListener={isMobile && deleteDisabled}
      >
        <span style={{ display: "inline-flex" }}>
          <IconButton
            aria-label={deleteDisabled ? deleteTooltip : "Delete"}
            onClick={() => {
              if (isMobile && deleteDisabled) {
                handleDeleteWrapClick();
              } else if (!deleteDisabled) {
                onDeleteClick(field);
              }
            }}
            disabled={!isMobile && deleteDisabled}
            size="small"
            color={inUse ? "default" : "error"}
            sx={{
              ...(isMobile ? { p: 0.25 } : {}),
              ...(deleteDisabled ? { color: "text.secondary", "&.Mui-disabled": { color: "text.secondary", opacity: 1 } } : {}),
            }}
          >
            <DeleteIcon fontSize="small" sx={isMobile ? { fontSize: 18 } : undefined} />
          </IconButton>
        </span>
      </Tooltip>
    </>
  );

  if (!isMobile) {
    return (
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 0 }}>
        {actions}
      </Box>
    );
  }
  return actions;
}

/** Row/card for a hidden transaction field. Same layout as visible field (icon, label, type chip, For) but with Unhide instead of Hide. */
export interface HiddenTransactionFieldRowProps {
  field: TransactionCustomField;
  forList: string;
  inUse: boolean;
  usageLoading?: boolean;
  saving: boolean;
  onEdit: (field: TransactionCustomField) => void;
  onUnhide: (field: TransactionCustomField) => void;
  onDeleteClick: (field: TransactionCustomField) => void;
  isMobile: boolean;
  readOnly?: boolean;
}

export function HiddenTransactionFieldRow({
  field,
  forList,
  inUse,
  usageLoading,
  saving,
  onEdit,
  onUnhide,
  onDeleteClick,
  isMobile,
  readOnly = false,
}: HiddenTransactionFieldRowProps): ReactElement {
  if (isMobile) {
    return (
      <Paper
        variant="outlined"
        sx={{
          p: { xs: 1.25, sm: 1 },
          border: 1,
          borderColor: "divider",
          borderRadius: 0.5,
          bgcolor: "background.paper",
        }}
      >
        <Stack spacing={0.5}>
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 0.5, flexWrap: "wrap" }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, minWidth: 0 }}>
              <TransactionFieldIcon icon={field.icon} sx={{ flexShrink: 0 }} />
              <Typography variant="body2" fontWeight="600" color="text.primary">
                {field.label}
              </Typography>
              <Chip
                label={field.type === "attachment" ? "Attachment" : "Text"}
                size="small"
                variant="outlined"
                sx={{ height: 20, borderColor: "divider", color: "text.secondary", "& .MuiChip-label": { px: 0.75, fontSize: "0.7rem" } }}
              />
            </Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.25 }}>
              <ActionsCellForHidden
                field={field}
                saving={saving}
                usageLoading={usageLoading}
                inUse={inUse}
                onEdit={onEdit}
                onUnhide={onUnhide}
                onDeleteClick={onDeleteClick}
                isMobile
                readOnly={readOnly}
              />
            </Box>
          </Box>
          <Typography variant="caption" color="text.secondary" sx={{ fontSize: "0.7rem", lineHeight: 1.3 }}>
            Hidden · For: {forList}
          </Typography>
        </Stack>
      </Paper>
    );
  }

  return (
    <TableRow hover sx={{ borderColor: "divider" }}>
      <TableCell sx={{ ...TRANSACTION_FIELD_TABLE_CELL_SX.order, verticalAlign: "middle" }} />
      <TableCell sx={{ ...TRANSACTION_FIELD_TABLE_CELL_SX.fieldName, verticalAlign: "middle" }} align="left">
        <Stack direction="row" alignItems="center" gap={1}>
          <TransactionFieldIcon icon={field.icon} sx={{ color: "action.active" }} />
          <Typography variant="body2" fontWeight="medium" color="text.primary">
            {field.label}
          </Typography>
        </Stack>
      </TableCell>
      <TableCell sx={{ ...TRANSACTION_FIELD_TABLE_CELL_SX.type, verticalAlign: "middle" }} align="left">
        <Chip
          label={field.type === "attachment" ? "Attachment" : "Text"}
          size="small"
          variant="outlined"
          sx={{ borderColor: "divider", color: "text.secondary" }}
        />
      </TableCell>
      <TableCell sx={{ ...TRANSACTION_FIELD_TABLE_CELL_SX.for, verticalAlign: "middle" }} align="left">
        <Typography variant="body2" color="text.secondary">
          Hidden · For: {forList}
        </Typography>
      </TableCell>
      <TableCell align="right" sx={{ ...TRANSACTION_FIELD_TABLE_CELL_SX.actions, verticalAlign: "middle" }}>
        <ActionsCellForHidden
          field={field}
          saving={saving}
          usageLoading={usageLoading}
          inUse={inUse}
          onEdit={onEdit}
          onUnhide={onUnhide}
          onDeleteClick={onDeleteClick}
          readOnly={readOnly}
        />
      </TableCell>
    </TableRow>
  );
}

export default function SortableTransactionFieldRow({
  field,
  forList,
  inUse,
  usageLoading,
  saving,
  onEdit,
  onHide,
  onDeleteClick,
  isMobile = false,
  readOnly = false,
}: SortableTransactionFieldRowProps): ReactElement {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: field.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  if (isMobile) {
    return (
      <Paper
        ref={setNodeRef}
        variant="outlined"
        sx={{
          p: { xs: 1.25, sm: 1 },
          ...style,
          border: 1,
          borderColor: isDragging ? "primary.main" : "divider",
          borderRadius: 0.5,
          bgcolor: "background.paper",
          cursor: isDragging ? "grabbing" : "grab",
        }}
      >
        <Stack spacing={0.5}>
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 0.5, flexWrap: "wrap" }}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 0.5,
                minWidth: 0,
                touchAction: "none",
              }}
              {...attributes}
              {...listeners}
            >
              <DragIndicatorIcon fontSize="small" sx={{ color: "text.secondary", flexShrink: 0, fontSize: 18 }} />
              <TransactionFieldIcon icon={field.icon} sx={{ flexShrink: 0 }} />
              <Typography variant="body2" fontWeight="600" color="text.primary">
                {field.label}
              </Typography>
              <Chip
                label={field.type === "attachment" ? "Attachment" : "Text"}
                size="small"
                variant="outlined"
                sx={{ height: 20, borderColor: "divider", color: "text.secondary", "& .MuiChip-label": { px: 0.75, fontSize: "0.7rem" } }}
              />
            </Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.25 }}>
              <ActionsCell
                field={field}
                saving={saving}
                usageLoading={usageLoading}
                inUse={inUse}
                onEdit={onEdit}
                onHide={onHide}
                onDeleteClick={onDeleteClick}
                isMobile
                readOnly={readOnly}
              />
            </Box>
          </Box>
          <Typography variant="caption" color="text.secondary" sx={{ fontSize: "0.7rem", lineHeight: 1.3 }}>
            For: {forList}
          </Typography>
        </Stack>
      </Paper>
    );
  }

  return (
    <TableRow
      ref={setNodeRef}
      style={style}
      hover
      sx={{
        cursor: isDragging ? "grabbing" : "grab",
      }}
    >
      <TableCell sx={{ ...TRANSACTION_FIELD_TABLE_CELL_SX.order, verticalAlign: "middle" }}>
        <Box
          {...attributes}
          {...listeners}
          sx={{
            display: "flex",
            alignItems: "center",
            cursor: isDragging ? "grabbing" : "grab",
            color: "text.secondary",
            "&:hover": { color: "text.primary" },
          }}
        >
          <DragIndicatorIcon fontSize="small" />
        </Box>
      </TableCell>
      <TableCell sx={{ ...TRANSACTION_FIELD_TABLE_CELL_SX.fieldName, verticalAlign: "middle" }} align="left">
        <Stack direction="row" alignItems="center" gap={1}>
          <TransactionFieldIcon icon={field.icon} sx={{ color: "action.active" }} />
          <Typography variant="body2" fontWeight="medium" color="text.primary">
            {field.label}
          </Typography>
        </Stack>
      </TableCell>
      <TableCell sx={{ ...TRANSACTION_FIELD_TABLE_CELL_SX.type, verticalAlign: "middle" }} align="left">
        <Chip
          label={field.type === "attachment" ? "Attachment" : "Text"}
          size="small"
          variant="outlined"
          sx={{ borderColor: "divider", color: "text.secondary" }}
        />
      </TableCell>
      <TableCell sx={{ ...TRANSACTION_FIELD_TABLE_CELL_SX.for, verticalAlign: "middle" }} align="left">
        <Typography variant="body2" color="text.secondary">
          {forList}
        </Typography>
      </TableCell>
      <TableCell align="right" sx={{ ...TRANSACTION_FIELD_TABLE_CELL_SX.actions, verticalAlign: "middle" }}>
        <ActionsCell
          field={field}
          saving={saving}
          usageLoading={usageLoading}
          inUse={inUse}
          onEdit={onEdit}
          onHide={onHide}
          onDeleteClick={onDeleteClick}
          readOnly={readOnly}
        />
      </TableCell>
    </TableRow>
  );
}
